import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccess } from '@/shared/rbac'
import { announcementsApi } from '../api/announcements.api'
import type { AnnouncementListFilters } from '../types'

const KEY = ['announcements'] as const

/** Bell badge + sidebar unread count. Only runs when the module is visible. */
export function useAnnouncementsUnreadCount() {
  const { canViewAnnouncements } = useAccess()
  return useQuery({
    queryKey: [...KEY, 'unread-count'],
    queryFn: () => announcementsApi.unreadCount(),
    enabled: canViewAnnouncements,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

export function useAnnouncementsList(filters: AnnouncementListFilters = {}) {
  const { canViewAnnouncements } = useAccess()
  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () => announcementsApi.list(filters),
    enabled: canViewAnnouncements,
    staleTime: 30_000,
  })
}

export function useAnnouncementDetail(id: string | undefined) {
  const { canViewAnnouncements } = useAccess()
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => announcementsApi.detail(id as string),
    enabled: canViewAnnouncements && !!id,
    staleTime: 30_000,
  })
}

/** Read / dismiss / acknowledge — invalidate list + unread count on success. */
export function useAnnouncementStateMutations() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY })

  return {
    markRead: useMutation({ mutationFn: (id: string) => announcementsApi.markRead(id), onSuccess: invalidate }),
    dismiss: useMutation({ mutationFn: (id: string) => announcementsApi.dismiss(id), onSuccess: invalidate }),
    acknowledge: useMutation({ mutationFn: (id: string) => announcementsApi.acknowledge(id), onSuccess: invalidate }),
  }
}
